import dayjs, { Dayjs } from 'dayjs'
import { getShipmentDisplayCalculated } from '../../../../helpers/entities/shipment/get-shipment-display-calculated'
import Machine from '../../../../types/api/machine'
import PrintQueueEntry from '../../../../types/api/print-queue-entry'

export interface PrintQueueEntryTiming {
    startAt: string
    endAt: string
}

function matchesMachine(a?: Machine, b?: Machine) {
    if (!a?.documentId || !b?.documentId) {
        return false
    }

    return String(a.documentId) === String(b.documentId)
}

function isFinishingMachineType(machine?: Machine) {
    const typeName = machine?.machine_type?.name?.toLowerCase() ?? ''
    return typeName.includes('finishing')
}

function getTiming(entry: PrintQueueEntry) {
    const shipment = entry.print_job?.shipment
    const additionalMachines = shipment?.additional_machines ?? []
    const entryMachine = entry.machine
    const mainMachine = shipment?.machine
    const isListedAdditional = additionalMachines.some(machine =>
        matchesMachine(machine, entryMachine)
    )

    const nonMainQueueCount = new Set(
        (entry.print_job?.print_queue_entries ?? [])
            .map(queueEntry => queueEntry.machine)
            .filter(
                (machine): machine is Machine =>
                    !!machine?.documentId &&
                    (!mainMachine || !matchesMachine(machine, mainMachine))
            )
            .map(machine => String(machine.documentId))
    ).size

    const spreadFinishing = additionalMachines.length === 1 && nonMainQueueCount > 1
    const isFinishingQueue =
        isListedAdditional ||
        (spreadFinishing &&
            !!entryMachine &&
            !!mainMachine &&
            !matchesMachine(entryMachine, mainMachine))

    return {
        entryMachine,
        mainMachine,
        isListedAdditional,
        isFinishingQueue,
        spreadFinishing,
        printingTime: getShipmentDisplayCalculated(shipment)?.printingTime ?? 0,
        finishingTime: getShipmentDisplayCalculated(shipment)?.finishingTime ?? 0,
        additionalCount: additionalMachines.length || 1,
    }
}

export function isQueueEntryFinishingTime(entry: PrintQueueEntry) {
    const timing = getTiming(entry)
    return timing.isFinishingQueue || isFinishingMachineType(timing.entryMachine)
}

export function getPrintQueueEntryDurationMinutes(entry: PrintQueueEntry) {
    const timing = getTiming(entry)

    if (!timing.isFinishingQueue) {
        return timing.printingTime
    }

    if (timing.spreadFinishing) {
        return timing.finishingTime
    }

    return timing.finishingTime / timing.additionalCount
}

export function hasEndTimeMachineMismatch(entry: PrintQueueEntry) {
    const timing = getTiming(entry)

    if (!timing.entryMachine) {
        return false
    }

    if (timing.isFinishingQueue || isFinishingMachineType(timing.entryMachine)) {
        return !timing.isListedAdditional || timing.spreadFinishing
    }

    if (timing.mainMachine && !matchesMachine(timing.mainMachine, timing.entryMachine)) {
        return true
    }

    return false
}

export function buildPrintQueueEndTimes(
    entries: PrintQueueEntry[],
    now: Dayjs = dayjs()
): Map<string, string> {
    const timings = buildPrintQueueEntryTimings(entries, now)
    const endTimes = new Map<string, string>()

    timings.forEach((timing, documentId) => {
        endTimes.set(documentId, timing.endAt)
    })

    return endTimes
}

export function buildPrintQueueEntryTimings(
    entries: PrintQueueEntry[],
    now: Dayjs = dayjs()
): Map<string, PrintQueueEntryTiming> {
    const timings = new Map<string, PrintQueueEntryTiming>()
    let previousEndAt: Dayjs | null = null

    entries.forEach((entry, index) => {
        const durationMinutes = getPrintQueueEntryDurationMinutes(entry)
        let startAt: Dayjs

        if (index === 0) {
            const activeExecutor = entry.print_job?.print_job_executors?.find(
                executor =>
                    !executor.end &&
                    executor.machine &&
                    matchesMachine(executor.machine, entry.machine)
            )
            startAt = activeExecutor ? dayjs(activeExecutor.start) : now
        } else {
            startAt = previousEndAt || now
        }

        const endAt = startAt.add(durationMinutes, 'minute')
        timings.set(entry.documentId, {
            startAt: startAt.toISOString(),
            endAt: endAt.toISOString(),
        })
        previousEndAt = endAt
    })

    return timings
}
